import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, ChevronRight, ChevronDown, Network, Plus, X, Search,
  ShoppingCart, Check, ExternalLink, Trash2, Package, Info, Tag,
} from 'lucide-react';
import { api } from '../api/client.ts';
import { InventoryItem, FacilitySupply, Category } from '../types/index.ts';
import { AddToOrderModal, ItemModal } from './Inventory.tsx';
import { getImageSrc } from '../utils/imageUrl.ts';
import { spinIconOut } from '../utils/iconAnimations.ts';
import '../styles/buttonAnimations.css';

// ─── Color palette (same 20-color set as Chat.tsx) ───────────────────────────
const ITEM_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#14b8a6',
  '#6366f1','#a855f7','#e11d48','#0ea5e9','#22c55e',
  '#eab308','#f43f5e','#7c3aed','#0891b2','#65a30d',
];
const getItemColor = (id: number) => ITEM_COLORS[id % ITEM_COLORS.length];

interface BOMNode {
  rel_id: number;
  id: number;
  name: string;
  part_number: string | null;
  unit_cost: number;
  supplier: string | null;
  quantity: number;
  notes: string | null;
  sourcing_status: 'sourced' | 'placeholder';
  children: BOMNode[];
}

interface UsedForPart {
  id: number;
  name: string;
  part_number: string | null;
  unit_cost: number;
  supplier: string | null;
  quantity: number | null;
  notes: string | null;
  thumbnail_path: string | null;
  matchLevel: 'exact' | 'brand' | 'type';
  sourcing_status: 'sourced' | 'placeholder';
}

// ─── Tree Node ───────────────────────────────────────────────────────────────
function TreeNode({
  node,
  depth,
  selectedId,
  onSelect,
}: {
  node: BOMNode;
  depth: number;
  selectedId: number | null;
  onSelect: (node: BOMNode) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const color = getItemColor(node.id);
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        onClick={() => onSelect(node)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: `5px 8px 5px ${8 + depth * 16}px`,
          borderRadius: 6,
          background: isSelected ? 'rgba(59,130,246,0.15)' : 'transparent',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{
            width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: hasChildren ? 'pointer' : 'default',
            color: hasChildren ? '#94a3b8' : 'transparent', flexShrink: 0, padding: 0,
          }}
        >
          {hasChildren
            ? (expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />)
            : null}
        </button>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{
          flex: 1, fontSize: 13,
          color: isSelected ? '#e2e8f0' : 'var(--text-secondary)',
          fontWeight: isSelected ? 600 : 400,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {node.name}
        </span>
        {node.unit_cost > 0 && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
            ${Number(node.unit_cost).toFixed(2)}
          </span>
        )}
      </div>
      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.rel_id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Category types for BOM picker sidebar -- deliberately named BomCategory, not Category:
// this file also imports the real Category type from types/index.ts (for ItemModal's categories
// prop, used by the Add Placeholder Part modal), and a same-named local interface here silently
// shadowed it, which built fine under `tsc --noEmit` locally but failed Vercel's `next build`-style
// full build (TS2440/TS2322) once something actually needed the real, wider Category shape. ─────
interface BomCategory {
  id: number;
  name: string;
  parent_id: number | null;
  icon_path: string | null;
}

interface CategoryNode extends BomCategory {
  children: CategoryNode[];
}

function buildCategoryTree(cats: BomCategory[]): CategoryNode[] {
  const map = new Map<number, CategoryNode>();
  cats.forEach((c) => map.set(c.id, { ...c, children: [] }));
  const roots: CategoryNode[] = [];
  cats.forEach((c) => {
    if (c.parent_id != null && map.has(c.parent_id)) {
      map.get(c.parent_id)!.children.push(map.get(c.id)!);
    } else if (c.parent_id == null) {
      roots.push(map.get(c.id)!);
    }
  });
  return roots;
}

// ─── Recursive category tree item ────────────────────────────────────────────
function CatTreeItem({
  node, depth, selectedCatId, onSelect, expandedIds, onToggle,
}: {
  node: CategoryNode; depth: number; selectedCatId: number | null;
  onSelect: (id: number) => void;
  expandedIds: Set<number>; onToggle: (id: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isActive = selectedCatId === node.id;
  const hasChildren = node.children.length > 0;
  const expanded = expandedIds.has(node.id);

  return (
    <div>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center',
          borderRadius: 'var(--radius-sm)',
          background: isActive ? 'rgba(59,130,246,0.12)' : hovered ? 'var(--bg-hover)' : 'transparent',
          transition: 'background var(--transition)',
        }}
      >
        {depth > 0 && <span style={{ width: depth * 12, flexShrink: 0, display: 'inline-block' }} />}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 30, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            <ChevronRight size={11} style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 150ms' }} />
          </button>
        ) : (
          <span style={{ width: 18, flexShrink: 0 }} />
        )}
        <button
          onClick={() => onSelect(node.id)}
          style={{
            flex: 1, border: 'none', background: 'none', cursor: 'pointer',
            padding: '8px 10px 8px 0',
            color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
            fontSize: '0.8125rem', fontWeight: isActive ? 600 : 400,
            textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {node.name}
        </button>
      </div>
      {hasChildren && expanded && node.children.map((child) => (
        <CatTreeItem key={child.id} node={child} depth={depth + 1} selectedCatId={selectedCatId} onSelect={onSelect} expandedIds={expandedIds} onToggle={onToggle} />
      ))}
    </div>
  );
}

// ─── Add Part Modal (catalogue-style) ────────────────────────────────────────
function AddPartModal({
  parentId,
  onClose,
  onLinked,
}: {
  parentId: number;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selectedCatId, setSelectedCatId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [expandedCatIds, setExpandedCatIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function toggleCat(id: number) {
    setExpandedCatIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const { data: catsRaw } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: () => api.get('/inventory/categories'),
  });
  const categories: BomCategory[] = (catsRaw as unknown as { data: BomCategory[] })?.data ?? [];
  const categoryTree = buildCategoryTree(categories);

  const { data: itemsRaw, isLoading } = useQuery({
    queryKey: ['bom-catalogue', selectedCatId, search],
    queryFn: () => api.get('/inventory/items', {
      ...(selectedCatId ? { categoryId: String(selectedCatId) } : {}),
      ...(search ? { search } : {}),
      limit: '60',
    }),
  });
  const items: InventoryItem[] = (itemsRaw as unknown as { data: InventoryItem[] })?.data ?? [];

  function toggleItem(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function linkParts() {
    if (selectedIds.size === 0) return;
    setSaving(true);
    setError('');
    try {
      for (const itemId of selectedIds) {
        await api.post(`/inventory/${parentId}/bom`, { childItemId: itemId, quantity: 1 });
      }
      onLinked();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to link parts');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      // zIndex 1000 (not the previous 400) -- BomPanel now also renders inside
      // SupplyDetailTakeover's own zIndex:500 page-takeover backdrop as the BOM tab, and this is a
      // plain position:fixed div (no portal), so it needs to clear that 500 regardless of which
      // context it's opened from.
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, width: 700, height: 560, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>Add Parts to BOM</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={16} /></button>
        </div>

        {/* Search bar */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              autoFocus
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelectedCatId(null); }}
              placeholder="Search parts…"
              style={{ width: '100%', padding: '7px 10px 7px 30px', background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Body: category sidebar + item grid */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Category tree sidebar */}
          <div style={{ width: 170, flexShrink: 0, borderRight: '1px solid var(--border-subtle)', overflowY: 'auto' }}>
            <div style={{ padding: '6px' }}>
              <button
                onClick={() => { setSelectedCatId(null); setSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center',
                  width: '100%', padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  background: selectedCatId === null && !search ? 'rgba(59,130,246,0.12)' : 'none',
                  border: 'none', cursor: 'pointer',
                  color: selectedCatId === null && !search ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  fontSize: '0.8125rem', fontWeight: selectedCatId === null && !search ? 600 : 400,
                  textAlign: 'left',
                }}
              >
                All Items
              </button>
              {categoryTree.map((node) => (
                <CatTreeItem
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedCatId={selectedCatId}
                  onSelect={(id) => { setSelectedCatId(id); setSearch(''); }}
                  expandedIds={expandedCatIds}
                  onToggle={toggleCat}
                />
              ))}
            </div>
          </div>

          {/* Item grid */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
            {isLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>}
            {!isLoading && items.filter((i) => i.id !== parentId).length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No items found</div>
            )}
            {!isLoading && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                {items.filter((i) => i.id !== parentId).map((item) => {
                  const isSelected = selectedIds.has(item.id);
                  const color = getItemColor(item.id);
                  const imgSrc = getImageSrc(item.thumbnail_path);
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggleItem(item.id)}
                      style={{
                        background: isSelected ? color + '12' : 'var(--bg-surface)',
                        border: `1px solid ${isSelected ? color + '55' : 'transparent'}`,
                        borderRadius: 8, overflow: 'hidden',
                        cursor: 'pointer', textAlign: 'left',
                        position: 'relative', display: 'flex', flexDirection: 'column',
                        padding: 0,
                        transition: 'background 0.12s, border-color 0.12s',
                        boxShadow: isSelected ? `0 0 0 1px ${color}44` : undefined,
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-elevated)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = isSelected ? color + '12' : 'var(--bg-surface)';
                      }}
                    >
                      {/* Image area */}
                      <div style={{
                        height: 90, flexShrink: 0, position: 'relative',
                        background: `linear-gradient(135deg, ${color}20, ${color}08)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={item.name}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 10 }}
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <Package size={34} color={color} style={{ opacity: 0.4 }} />
                        )}
                        {isSelected && (
                          <div style={{ position: 'absolute', top: 6, right: 6, width: 18, height: 18, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={11} color="#fff" />
                          </div>
                        )}
                      </div>
                      {/* Card body */}
                      <div style={{ padding: '8px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'], overflow: 'hidden', lineHeight: 1.35 }}>{item.name}</div>
                        {item.part_number && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>#{item.part_number}</div>}
                        {item.supplier && <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.supplier}</div>}
                        {item.price > 0 && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginTop: 2 }}>${Number(item.price).toFixed(2)}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          {error
            ? <span style={{ flex: 1, fontSize: 12, color: '#f87171' }}>{error}</span>
            : <span style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>{selectedIds.size > 0 ? `${selectedIds.size} part${selectedIds.size !== 1 ? 's' : ''} selected` : 'Click parts to select'}</span>
          }
          <button
            className="combo2"
            onClick={linkParts}
            disabled={saving || selectedIds.size === 0}
            style={{ padding: '8px 22px', background: selectedIds.size === 0 ? 'var(--bg-elevated)' : '#3b82f6', border: 'none', borderRadius: 6, color: selectedIds.size === 0 ? 'var(--text-muted)' : '#fff', fontSize: 13, fontWeight: 600, cursor: selectedIds.size === 0 || saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Adding…' : `Add ${selectedIds.size > 0 ? selectedIds.size + ' ' : ''}Part${selectedIds.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BOM Panel — the actual tree/detail UI, parameterized by a supply id rather than reading
// useParams directly, so it can be reused both by the standalone /inventory/:id/bom route below
// (kept for any deep links) and as a tab inside SupplyDetailTakeover in Inventory.tsx. Still
// fetches everything itself (facility-supply, bom tree, order/relationship mutations) — just
// parameterized by supplyId instead of pulling it from the URL. ──────────────────────────────
// isConcentrator: facility_supplies and facility_concentrators are separate tables with their own
// id sequences, so a concentrator's id can (and for 8 of 22 today, does) collide with an unrelated
// supply's id. Without this flag the id-only lookups below would silently resolve to whichever
// table matched first, showing that unrelated item's parts. Set by the standalone route below from
// ?type=concentrator (added to the concentrator card's own link in Inventory.tsx); the
// SupplyDetailTakeover tab usage never passes it, since that path is supplies only.
export function BomPanel({ supplyId, isConcentrator = false }: { supplyId: number; isConcentrator?: boolean }) {
  const id = String(supplyId);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [selectedNode, setSelectedNode] = useState<BOMNode | null>(null);
  const [orderQty, setOrderQty] = useState(1);
  const [showAddPart, setShowAddPart] = useState(false);
  const [addParentId, setAddParentId] = useState<number | null>(null);
  const [showAddPlaceholder, setShowAddPlaceholder] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [selectedUsedForPart, setSelectedUsedForPart] = useState<UsedForPart | null>(null);
  const [orderItem, setOrderItem] = useState<InventoryItem | null>(null);

  // Sync order qty to selected node's BOM quantity when selection changes
  useEffect(() => {
    if (selectedNode) setOrderQty(selectedNode.quantity);
  }, [selectedNode?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Concentrators have no "name" field the way facility_supplies rows do -- just manufacturer/model
  // -- so a concentrator fetches its own small record and synthesizes a FacilitySupply-shaped
  // object for the handful of display reads below (name/supply_type/brand), rather than touching
  // every one of those read sites individually.
  const { data: itemRaw, isLoading: itemLoading } = useQuery({
    queryKey: ['facility-supply', id],
    queryFn: () => api.get<FacilitySupply>(`/inventory/supplies/${id}`),
    enabled: !!id && !isConcentrator,
  });
  const { data: concRaw, isLoading: concLoading } = useQuery({
    queryKey: ['concentrator', id],
    queryFn: () => api.get<{ id: number; manufacturer: string | null; model: string | null }>(`/concentrators/${id}`),
    enabled: !!id && isConcentrator,
  });
  const concentrator = (concRaw as unknown as { data: { manufacturer: string | null; model: string | null } })?.data;
  const rootItem: FacilitySupply | undefined = isConcentrator
    ? concentrator
      // supply_type is cast past FacilitySupply's real union (which has no 'concentrator' member)
      // -- rootItem is read-only display text in this file, never round-tripped back to the API.
      ? ({ name: [concentrator.manufacturer, concentrator.model].filter(Boolean).join(' ') || 'Concentrator', supply_type: 'concentrator', brand: concentrator.manufacturer ?? null, model: concentrator.model ?? null } as unknown as FacilitySupply)
      : undefined
    : (itemRaw as unknown as { data: FacilitySupply })?.data;

  // Best-guess exact "Used For" tag for THIS equipment, to pre-fill a new placeholder's used_for
  // with (see the Add Placeholder Part modal below) -- mirrors the tag format GET /inventory/:id/bom
  // itself matches against server-side (inventory.ts: "${supply_type}:${brand}:${model}", with the
  // 'other'-category-slug variant for custom "Other Equipment" categories). Falls back to a
  // brand-level or type-level tag when brand/model aren't both set, same graceful degradation the
  // server's own match_level ('exact'/'brand'/'type') already expresses.
  const defaultUsedForTag = (() => {
    if (!rootItem) return null;
    const supplyType = rootItem.supply_type as string;
    const brand = rootItem.brand?.trim() || null;
    const model = rootItem.model?.trim() || null;
    const typeKey = supplyType === 'other' && rootItem.category?.trim()
      ? rootItem.category.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
      : supplyType;
    if (!typeKey) return null;
    if (brand && model) return `${typeKey}:${brand}:${model}`;
    if (brand) return `${typeKey}:${brand}:all`;
    return `${typeKey}:all`;
  })();

  const { data: bomRaw, isLoading: bomLoading } = useQuery({
    queryKey: ['bom', id, isConcentrator],
    queryFn: async () => {
      const data = await api.get(`/inventory/${id}/bom${isConcentrator ? '?type=concentrator' : ''}`);
      console.log('[BOMView] response data:', data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      console.log('[BOMView] usedForParts:', (data as any)?.usedForParts);
      return data;
    },
    enabled: !!id,
  });
  const bomTree: BOMNode[] = (bomRaw as unknown as { data: BOMNode[] })?.data ?? [];
  const usedForParts: UsedForPart[] = (bomRaw as unknown as { usedForParts: UsedForPart[] })?.usedForParts ?? [];

  // Only fetched when the Add Placeholder Part modal is actually open -- ItemModal needs the
  // full category list for its Category picker, same as AddPartModal's own catalogue browser.
  const { data: categoriesRaw } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: () => api.get('/inventory/categories'),
    enabled: showAddPlaceholder,
  });
  const categories: Category[] = (categoriesRaw as unknown as { data: Category[] })?.data ?? [];

  const { data: usedForItemRaw } = useQuery({
    queryKey: ['inventory-item', selectedUsedForPart?.id],
    queryFn: () => api.get<InventoryItem>(`/inventory/items/${selectedUsedForPart!.id}`),
    enabled: !!selectedUsedForPart,
  });
  const usedForItem = (usedForItemRaw as unknown as { data: InventoryItem })?.data ?? null;

  const color = selectedNode ? getItemColor(selectedNode.id) : '#3b82f6';
  const orderTotal = selectedNode && selectedNode.unit_cost > 0
    ? (orderQty * Number(selectedNode.unit_cost)).toFixed(2)
    : null;

  async function placeOrder() {
    if (!selectedNode) return;
    setOrdering(true);
    try {
      await api.post('/orders', {
        inventory_item_id: selectedNode.id,
        product_name: selectedNode.name,
        part_number: selectedNode.part_number,
        category: rootItem?.supply_type,
        quantity: orderQty,
        unit_price: selectedNode.unit_cost || null,
        ordered_from: selectedNode.supplier || 'TBD',
        status: 'pending',
      });
      setOrderSuccess(true);
      setTimeout(() => setOrderSuccess(false), 2500);
    } catch { /* ignore */ } finally {
      setOrdering(false);
    }
  }

  async function removeRelationship(relId: number) {
    try {
      await api.delete(`/inventory/bom/${relId}`);
      if (selectedNode?.rel_id === relId) setSelectedNode(null);
      qc.invalidateQueries({ queryKey: ['bom', id] });
    } catch { /* ignore */ }
  }

  if ((isConcentrator ? concLoading : itemLoading) || bomLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Split panel — height comes from the caller (SupplyDetailTakeover's BOM tab, or the
          standalone route wrapper below) via a bounded container, so the tree and detail columns
          each get their own independent scroll instead of the whole page scrolling as one. */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

        {/* ── Left: Parts Tree (280px) ── */}
        <div style={{
          width: 280, flexShrink: 0,
          background: 'var(--bg-surface)',
          borderRight: '1px solid var(--border-subtle)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0,
        }}>
          {/* Panel header */}
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
              Parts Tree
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {rootItem?.name}
            </div>
          </div>

          {/* Tree */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 6px', minHeight: 0 }}>
            {bomTree.length === 0 && usedForParts.length === 0 ? (
              <div style={{ padding: '28px 10px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>
                No parts linked yet.<br />Click "Add Part" below.
              </div>
            ) : (
              <>
                {bomTree.map((node) => (
                  <TreeNode
                    key={node.rel_id}
                    node={node}
                    depth={0}
                    selectedId={selectedNode?.id ?? null}
                    onSelect={(node) => { setSelectedNode(node); setSelectedUsedForPart(null); }}
                  />
                ))}
                {usedForParts.length > 0 && (
                  <>
                    <div style={{ margin: '8px 4px 4px', borderTop: bomTree.length > 0 ? '1px solid var(--border-subtle)' : 'none', paddingTop: bomTree.length > 0 ? 8 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        <Info size={10} /> Linked via Used For
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                        Parts with this equipment as their target
                      </div>
                    </div>
                    {usedForParts.map((part) => {
                      const badge = part.matchLevel === 'exact'
                        ? { label: 'Exact match', color: '#10b981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)' }
                        : part.matchLevel === 'brand'
                        ? { label: `All ${rootItem?.brand ?? ''}`, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' }
                        : { label: `All ${rootItem?.supply_type ?? ''}`, color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' };
                      const isUsedForSelected = selectedUsedForPart?.id === part.id;
                      // Persistent baseline tint for placeholder rows -- a very light gray wash
                      // (0.06) across the whole row, deliberately a DIFFERENT, much subtler color
                      // than the "Not sourced" badge itself (now amber, badge-amber token -- see
                      // below) -- this is "this row is a placeholder" background context, not the
                      // badge's own color, so it reads as a subtle always-on background rather than
                      // competing with the badge or the blue hover/selected treatment, both of which
                      // still take visual priority over this when active (hover overwrites it via
                      // the mouse handlers below; selected's blue wins via the ternary here).
                      const restBg = isUsedForSelected
                        ? 'rgba(59,130,246,0.15)'
                        : part.sourcing_status === 'placeholder' ? 'rgba(107,114,128,0.06)' : 'transparent';
                      return (
                        <div
                          key={part.id}
                          onClick={() => { setSelectedUsedForPart(part); setSelectedNode(null); }}
                          style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '7px 8px', borderRadius: 6, cursor: 'pointer', background: restBg, borderLeft: isUsedForSelected ? '2px solid #3b82f6' : '2px solid transparent' }}
                          onMouseEnter={(e) => { if (!isUsedForSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = restBg; }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                              {part.name}
                            </span>
                            {/* Item A: "Not sourced" moved out of this row entirely -- it now
                                stacks in its own row directly underneath (below), right-aligned
                                into the SAME column as this badge (round 3 fix -- round 2's
                                version left the row below at the default flex-start, which put
                                it under the title text on the left instead of under this badge
                                on the right). */}
                            <span style={{ fontSize: 9, fontWeight: 600, color: badge.color, background: badge.bg, border: `1px solid ${badge.border}`, borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {badge.label}
                            </span>
                          </div>
                          {part.sourcing_status === 'placeholder' && (
                            // DESIGN_SYSTEM.md §22 badge-amber token (rgba(245,158,11,0.15) bg /
                            // #fbbf24 text) -- reused inline since this component's chips are all
                            // hand-rolled, not the className="badge badge-amber" pair.
                            // justifyContent: 'flex-end' is the round-3 fix -- right-aligns this
                            // row's content to the same right edge the "Exact match" badge above
                            // sits at (that row is justify-content: space-between with the badge
                            // flexShrink: 0 on the right), so the two badges form one right-aligned
                            // vertical stack instead of this one sitting left, under the title.
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <span style={{ fontSize: 9, fontWeight: 600, color: '#fbbf24', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                                Not sourced
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                            {part.part_number && <span>#{part.part_number}</span>}
                            {part.supplier && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{part.supplier}</span>}
                            {part.unit_cost > 0 && <span>${Number(part.unit_cost).toFixed(2)}</span>}
                            {part.quantity != null && <span>Qty: {part.quantity}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>

          {/* Add Part button */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            <button
              className="combo2"
              onMouseLeave={spinIconOut}
              onClick={() => {
                setAddParentId(selectedNode?.id ?? parseInt(id ?? '0'));
                setShowAddPart(true);
              }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '8px 0', background: '#3b82f6',
                border: 'none', borderRadius: 6,
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <Plus size={14} className="btn-icon" /> Add Part
            </button>
            {/* Add Placeholder Part -- deliberately distinct styling (ghost + dashed border) from
                the solid "Add Part" button above: that one links an existing catalogued item to
                this one physical unit via parts_relationships; this one creates a new model/brand-
                scoped stub via used_for (same mechanism as the "All wheelchair"/"All Medline" tags),
                so it shows up in every matching equipment's Parts/BOM tab, not just this one. */}
            <button
              className="combo1"
              onMouseLeave={spinIconOut}
              onClick={() => setShowAddPlaceholder(true)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                marginTop: 8, padding: '8px 0', background: 'transparent',
                border: '1px dashed var(--border-default)', borderRadius: 6,
                color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <Tag size={14} className="btn-icon" /> Add Placeholder Part
            </button>
          </div>
        </div>

        {/* ── Right: Detail panel ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px', minHeight: 0 }}>
          {!selectedNode && !selectedUsedForPart ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', color: 'var(--text-muted)', gap: 14,
            }}>
              <Network size={44} style={{ opacity: 0.15 }} />
              <span style={{ fontSize: 14 }}>Select a part from the tree to view details</span>
            </div>
          ) : selectedUsedForPart && !selectedNode ? (
            /* ── Used For part detail ── */
            <div>
              {/* Match level badge */}
              {(() => {
                const badge = selectedUsedForPart.matchLevel === 'exact'
                  ? { label: 'Exact match', color: '#10b981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)' }
                  : selectedUsedForPart.matchLevel === 'brand'
                  ? { label: `All ${rootItem?.brand ?? ''}`, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' }
                  : { label: `All ${rootItem?.supply_type ?? ''}`, color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' };
                return (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '3px 12px', background: badge.bg,
                      border: `1px solid ${badge.border}`, borderRadius: 999,
                      fontSize: 12, fontWeight: 600, color: badge.color,
                    }}>
                      <Info size={11} /> Linked via Used For · {badge.label}
                    </div>
                    {selectedUsedForPart.sourcing_status === 'placeholder' && (
                      // Same badge-amber token as the tree-row chip above (DESIGN_SYSTEM.md §22)
                      // -- this detail-panel pill was still the old low-contrast gray; recolored
                      // for consistency so "Not sourced" reads the same everywhere it appears.
                      // Layout untouched here (flexWrap already wraps it under on narrow space) --
                      // the task's stack-under-"Exact match" ask was specifically about the
                      // compact tree-row chip above, not this larger descriptive pill.
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 12px', background: 'rgba(245,158,11,0.15)',
                        border: '1px solid rgba(245,158,11,0.3)', borderRadius: 999,
                        fontSize: 12, fontWeight: 600, color: '#fbbf24',
                      }}>
                        Not sourced
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Thumbnail */}
              {getImageSrc(selectedUsedForPart.thumbnail_path) && (
                <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                  <img
                    src={getImageSrc(selectedUsedForPart.thumbnail_path)!}
                    alt={selectedUsedForPart.name}
                    style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', padding: 8 }}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}

              {/* Supplier badge */}
              {selectedUsedForPart.supplier && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '3px 12px', background: 'rgba(59,130,246,0.12)',
                  border: '1px solid rgba(59,130,246,0.25)', borderRadius: 999,
                  fontSize: 12, fontWeight: 600, color: '#60a5fa', marginBottom: 16,
                }}>
                  {selectedUsedForPart.supplier}
                </div>
              )}

              {/* Item name */}
              <h2 style={{ margin: '0 0 4px', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {selectedUsedForPart.name}
              </h2>
              {selectedUsedForPart.part_number && (
                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 22 }}>
                  Part #{selectedUsedForPart.part_number}
                </div>
              )}
              {!selectedUsedForPart.part_number && <div style={{ marginBottom: 22 }} />}

              {/* Metric cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 22 }}>
                {[
                  { label: 'Unit Price', value: selectedUsedForPart.unit_cost > 0 ? `$${Number(selectedUsedForPart.unit_cost).toFixed(2)}` : '—' },
                  { label: 'Qty in Stock', value: selectedUsedForPart.quantity != null ? String(selectedUsedForPart.quantity) : '—' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Info card */}
              <div className="card" style={{ marginBottom: 20 }}>
                {usedForItem?.brand && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Brand</span>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{usedForItem.brand}</span>
                  </div>
                )}
                {usedForItem?.location && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: selectedUsedForPart.notes ? '1px solid var(--border-subtle)' : 'none' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Location</span>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{usedForItem.location}</span>
                  </div>
                )}
                {selectedUsedForPart.notes && (
                  <div style={{ padding: '10px 0 2px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Notes</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{selectedUsedForPart.notes}</div>
                  </div>
                )}
                {!usedForItem?.brand && !usedForItem?.location && !selectedUsedForPart.notes && (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '4px 0' }}>No additional details loaded</div>
                )}
              </div>

              {/* Description */}
              {usedForItem?.product_description && (
                <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, padding: '12px 14px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
                  {usedForItem.product_description}
                </div>
              )}

              {/* Product link */}
              {usedForItem?.product_link && (
                <div style={{ marginBottom: 16 }}>
                  <a href={usedForItem.product_link} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 13, color: '#60a5fa', display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}>
                    <ExternalLink size={12} /> Product Link
                  </a>
                </div>
              )}

              {/* Category */}
              {usedForItem?.category_name && (
                <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>Category</span>
                  <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.25)', fontWeight: 500 }}>{usedForItem.category_name}</span>
                </div>
              )}

              {/* Used For tags */}
              {usedForItem?.used_for && usedForItem.used_for.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6 }}>Used For</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {usedForItem.used_for.map((tag, i) => (
                      <span key={i} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)', fontWeight: 500 }}>{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                {usedForItem && (
                  <button
                    className="combo2"
                    onMouseLeave={spinIconOut}
                    onClick={() => setOrderItem(usedForItem)}
                    style={{
                      flex: 1, padding: '10px 0', background: '#3b82f6',
                      border: 'none', borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    <ShoppingCart size={15} className="btn-icon" /> Order
                  </button>
                )}
                <button
                  className="combo1"
                  onMouseLeave={spinIconOut}
                  onClick={() => navigate(`/inventory/item/${selectedUsedForPart.id}`)}
                  style={{
                    flex: 1, padding: '10px 0', background: 'transparent',
                    border: '1px solid var(--border-default)', borderRadius: 6,
                    color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  <ExternalLink size={15} className="btn-icon" /> View Full Item
                </button>
              </div>
            </div>
          ) : selectedNode ? (
            /* ── Tree node detail ── */
            <div style={{ maxWidth: 560 }}>
              {/* Supplier badge */}
              {selectedNode.supplier && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '3px 12px', background: color + '20',
                  border: `1px solid ${color}44`, borderRadius: 999,
                  fontSize: 12, fontWeight: 600, color, marginBottom: 16,
                }}>
                  {selectedNode.supplier}
                </div>
              )}

              {/* Item name */}
              <h2 style={{ margin: '0 0 4px', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {selectedNode.name}
              </h2>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 22, display: 'flex', gap: 8, alignItems: 'center' }}>
                {selectedNode.part_number && <span>Part #{selectedNode.part_number}</span>}
                {selectedNode.part_number && <span>·</span>}
                <span>BOM qty: <strong style={{ color: 'var(--text-secondary)' }}>{selectedNode.quantity}</strong></span>
              </div>

              {/* Metric cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 22 }}>
                {[
                  { label: 'Unit Price', value: selectedNode.unit_cost > 0 ? `$${Number(selectedNode.unit_cost).toFixed(2)}` : '—' },
                  { label: 'Total (unit × BOM qty)', value: selectedNode.unit_cost > 0 ? `$${(Number(selectedNode.unit_cost) * selectedNode.quantity).toFixed(2)}` : '—' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Info card */}
              <div className="card" style={{ marginBottom: 20 }}>
                {selectedNode.supplier && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Supplier</span>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{selectedNode.supplier}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: selectedNode.notes ? '1px solid var(--border-subtle)' : 'none' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sub-parts</span>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{selectedNode.children.length}</span>
                </div>
                {selectedNode.notes && (
                  <div style={{ padding: '10px 0 2px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Notes</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{selectedNode.notes}</div>
                  </div>
                )}
              </div>

              {/* Order card -- placeholder (not-yet-sourced) nodes get a redirect message instead
                  of a working order form, same treatment as AddToOrderModal. */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
                  Order
                </div>
                {selectedNode.sourcing_status === 'placeholder' ? (
                  <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: 'var(--accent-warning)', fontSize: 13, marginBottom: 16 }}>
                    ⚠ Not sourced yet — no supplier or pricing on file, so it can't be ordered. Use "View Item →" below to edit it and "Enter into Inventory" once a supplier's been found.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <label style={{ fontSize: 13, color: 'var(--text-secondary)', minWidth: 110 }}>Order quantity</label>
                      <input
                        type="number" min={1} value={orderQty}
                        onChange={(e) => setOrderQty(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{
                          width: 80, padding: '6px 10px',
                          background: 'var(--bg-base)', border: '1px solid var(--border-default)',
                          borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, outline: 'none',
                        }}
                      />
                    </div>
                    {orderTotal !== null && (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                        Order total: <strong style={{ color: 'var(--text-primary)' }}>${orderTotal}</strong>
                      </div>
                    )}
                    {orderSuccess && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                        background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                        borderRadius: 6, color: '#4ade80', fontSize: 13, marginBottom: 12,
                      }}>
                        <Check size={14} /> Added to orders!
                      </div>
                    )}
                    <button
                      className="combo2"
                      onMouseLeave={spinIconOut}
                      onClick={placeOrder}
                      disabled={ordering}
                      style={{
                        width: '100%', padding: '10px 0', background: '#3b82f6',
                        border: 'none', borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 600,
                        cursor: ordering ? 'default' : 'pointer', opacity: ordering ? 0.7 : 1,
                        marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}
                    >
                      <ShoppingCart size={15} className="btn-icon" /> {ordering ? 'Adding…' : 'Add to Order'}
                    </button>
                  </>
                )}
                <button
                  className="combo1"
                  onMouseLeave={spinIconOut}
                  onClick={() => navigate(`/inventory/item/${selectedNode.id}`)}
                  style={{
                    width: '100%', padding: '8px 0', background: 'transparent',
                    border: '1px solid var(--border-default)', borderRadius: 6,
                    color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <ExternalLink size={13} className="btn-icon" /> View Item →
                </button>
              </div>

              {/* Remove from BOM */}
              <button
                className="combo3-danger"
                onMouseLeave={spinIconOut}
                onClick={() => removeRelationship(selectedNode.rel_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  background: 'transparent', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 6, color: '#f87171', fontSize: 12, cursor: 'pointer',
                }}
              >
                <Trash2 size={13} className="btn-icon" /> Remove from BOM
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Add Part Modal */}
      {showAddPart && addParentId !== null && (
        <AddPartModal
          parentId={addParentId}
          onClose={() => setShowAddPart(false)}
          onLinked={() => {
            setShowAddPart(false);
            qc.invalidateQueries({ queryKey: ['bom', id] });
          }}
        />
      )}

      {/* Add Placeholder Part Modal -- same Add/Edit Item form + BomSelector as the main catalogue,
          just seeded with sourcing_status:'placeholder' so it doesn't require supplier/price/link.
          ROOT CAUSE FIX (confirmed against live data, not guessed): a placeholder added here used
          to get NO initial `used_for` tag at all -- `initial` only ever set sourcing_status. This
          button's own existing comment above says the point of a placeholder is to "show up in
          every matching equipment's Parts/BOM tab" via a used_for tag, same mechanism as any other
          "Used For" link -- but nothing ever actually wrote that tag unless the person manually
          went and set it themselves in the generic BomSelector field, with no indication they
          needed to. A real placeholder Chris added this way (items.id=796, "Joerns , 4 INCH
          LENGTH EXTENSION KIT, UCXT") has used_for=[] in the live DB to this day -- a real orphan,
          not a query/filter excluding it (GET /inventory/:id/bom's used-for query, confirmed by
          reading it, has no sourcing_status filter at all; BOMView's own render code actively
          tints and badges placeholder rows rather than hiding them). Pre-filling the exact tag for
          THIS equipment here closes that gap for every new placeholder going forward -- the
          BomSelector field stays fully editable, so this is a default, not a lock-in. */}
      {showAddPlaceholder && (
        <ItemModal
          initial={{ sourcing_status: 'placeholder', used_for: defaultUsedForTag ? [defaultUsedForTag] : [] }}
          categories={categories}
          onClose={() => setShowAddPlaceholder(false)}
          onSaved={() => {
            setShowAddPlaceholder(false);
            qc.invalidateQueries({ queryKey: ['bom', id] });
          }}
        />
      )}

      {/* Order modal */}
      {orderItem && <AddToOrderModal item={orderItem} onClose={() => setOrderItem(null)} />}
    </div>
  );
}

// ─── Standalone /inventory/:id/bom route — kept for any existing deep links, no longer the path
// SupplyDetailTakeover's "Parts / BOM" action uses (that's a tab now, see Inventory.tsx). Just a
// thin breadcrumb + bordered page shell wrapping the same BomPanel above. ─────────────────────
export default function BOMView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Concentrator's own card links here with ?type=concentrator (Inventory.tsx) so this breadcrumb
  // fetch, and BomPanel below, know which table `id` refers to -- see the isConcentrator comment
  // on BomPanel above for why that matters.
  const isConcentrator = searchParams.get('type') === 'concentrator';

  const { data: itemRaw } = useQuery({
    queryKey: ['facility-supply', id],
    queryFn: () => api.get<FacilitySupply>(`/inventory/supplies/${id}`),
    enabled: !!id && !isConcentrator,
  });
  const { data: concRaw } = useQuery({
    queryKey: ['concentrator', id],
    queryFn: () => api.get<{ id: number; manufacturer: string | null; model: string | null }>(`/concentrators/${id}`),
    enabled: !!id && isConcentrator,
  });
  const concentrator = (concRaw as unknown as { data: { manufacturer: string | null; model: string | null } })?.data;
  const rootItem: FacilitySupply | undefined = isConcentrator
    ? concentrator
      ? ({ name: [concentrator.manufacturer, concentrator.model].filter(Boolean).join(' ') || 'Concentrator' } as unknown as FacilitySupply)
      : undefined
    : (itemRaw as unknown as { data: FacilitySupply })?.data;

  if (!id) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
      {/* Breadcrumb header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 20px', borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)', flexShrink: 0,
      }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            // Concentrators live under the Supplies tab's own Concentrators sub-tab, restored from
            // FacilitySuppliesTab's own sessionStorage key (inventory_supplies_state / activeType)
            // -- reusing that existing mechanism rather than a made-up tab id the Supplies tab's
            // sub-tab state doesn't read. supplyId is meaningless for a concentrator id (it would
            // ask the Supplies detail lookup to resolve the wrong table's row), so it's omitted.
            if (isConcentrator) {
              try { sessionStorage.setItem('inventory_supplies_state', JSON.stringify({ activeType: 'concentrators' })); } catch { /* per-viewer convenience only */ }
              navigate('/inventory?tab=facility-supplies');
            } else {
              navigate(`/inventory?tab=facility-supplies&supplyId=${id}`);
            }
          }}
          style={{ gap: 6 }}
        >
          <ArrowLeft size={14} /> {rootItem?.name ?? 'Item'}
        </button>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Bill of Materials</span>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        <BomPanel supplyId={Number(id)} isConcentrator={isConcentrator} />
      </div>
    </div>
  );
}
